import type {AxiosError} from "axios";



import {persistSessionStoreSync, useSessionStore} from "@/src/modules/auth/store/session";
import {stampSessionLoginAt} from "@/src/modules/auth/lib/stamp-session-login-at";

import CoreService from "@/src/modules/core/services/core";

import {meService} from "@/src/modules/core/services/me";

import type {Me} from "@/src/modules/core/types";



import {supportImpersonateApi} from "../api/impersonate";

import {

  clearImpersonationSessionFlags,

  clearImpersonationTokenBackup,

  getAdminSessionBackup,
  getAdminTokenBackup,
  getImpersonationTokenBackup,
  isClientUsingImpersonationBearer,
  isImpersonationActiveFlag,
  setAdminSessionBackup,
  setImpersonationActiveFlag,
  setImpersonationTokenBackup,
} from "../lib/impersonation-session";

import type {ImpersonationStatus, StartImpersonationPayload} from "../types";

import {IMPERSONATION_RESUME_REASON} from "../types";



type ActiveImpersonationStatus = Extract<ImpersonationStatus, {active: true}>;



class ImpersonationService extends CoreService {

  getCanAccessSupportTools(me: Me | null): boolean {

    if (!me || me.impersonation) return false;

    return Boolean(me.isAdmin || me.isSuperAdmin);

  }



  async checkFeatureEnabled(): Promise<boolean> {
    try {
      await supportImpersonateApi.getStatus();
      return true;
    } catch (error) {
      const status = (error as AxiosError)?.response?.status;
      // 403 = SUPPORT_IMPERSONATION_ENABLED off or not allowed
      if (status === 403) return false;
      throw error;
    }
  }



  private applyImpersonationSession(accessToken: string, expiresAt: string) {

    setImpersonationTokenBackup(accessToken);

    useSessionStore.getState().setSession(
      stampSessionLoginAt({
        access_token: accessToken,
        token_type: "Bearer",
        expires_at: expiresAt,
      }),
    );

    setImpersonationActiveFlag(true);

  }



  private ensureAdminBackupFromSession(): string | null {
    const existing = getAdminTokenBackup();
    if (existing) return existing;

    const session = useSessionStore.getState().session;
    const sessionToken = session?.access_token;
    if (!sessionToken) return null;

    const impersonationBackup = getImpersonationTokenBackup();
    if (impersonationBackup && sessionToken === impersonationBackup) {
      return null;
    }
    if (isClientUsingImpersonationBearer()) {
      return null;
    }

    setAdminSessionBackup(session);
    return sessionToken;
  }



  /** True when the app bearer is already the impersonation token (not admin). */

  private isSessionUsingImpersonationBearer(adminToken: string | null): boolean {

    const currentToken = useSessionStore.getState().session?.access_token;

    if (!currentToken) return false;

    if (!adminToken) return Boolean(getImpersonationTokenBackup());

    return currentToken !== adminToken;

  }



  private async resumeImpersonationFromServer(status: ActiveImpersonationStatus): Promise<void> {

    try {

      await supportImpersonateApi.end();

    } catch {

      // Recovery end with admin bearer may fail if the server already cleared the session.

    }



    const response = await supportImpersonateApi.start({

      target_user_id: status.target.user_id,

      reason: IMPERSONATION_RESUME_REASON,

    });

    const data = response.data.data;



    this.applyImpersonationSession(data.access_token, data.expires_at);

  }



  async start(payload: StartImpersonationPayload) {

    const session = useSessionStore.getState().session;

    if (!session?.access_token) {

      throw new Error("No active session");

    }

    const reason = payload.reason?.trim();

    if (!reason) {

      throw new Error("Reason is required");

    }

    if (!payload.target_user_id) {

      throw new Error("Target user is required");

    }



    setAdminSessionBackup(session);



    const response = await supportImpersonateApi.start({

      ...payload,

      reason,

    });

    const data = response.data.data;



    this.applyImpersonationSession(data.access_token, data.expires_at);

    return meService.getMe();

  }



  async end() {

    await supportImpersonateApi.end();

    return this.restoreAdminSession();

  }



  async restoreAdminSession() {
    const adminSession = getAdminSessionBackup();
    if (!adminSession?.access_token) {
      clearImpersonationSessionFlags();
      throw new Error(
        "Admin session backup is missing. Sign in again to continue.",
      );
    }

    useSessionStore.getState().setSession(adminSession);
    persistSessionStoreSync();

    const me = await meService.getMe();
    clearImpersonationSessionFlags();
    persistSessionStoreSync();

    return me;
  }



  /**

   * Reconcile client bearer with server impersonation state.

   * Returns true when the session bearer was swapped (caller should refetch /me via React Query).

   * Uses admin bearer only for GET /status; all other API calls use the impersonation token.

   */

  async recoverImpersonationSession(me: Me | null): Promise<boolean> {

    const session = useSessionStore.getState().session;

    if (!session?.access_token) return false;



    if (me?.impersonation?.active) {

      const adminToken = getAdminTokenBackup();

      if (this.isSessionUsingImpersonationBearer(adminToken)) {

        setImpersonationActiveFlag(true);

        const token = session.access_token;

        if (!getImpersonationTokenBackup()) {

          setImpersonationTokenBackup(token);

        }

      }

      return false;

    }



    const hasImpersonationArtifacts =

      isImpersonationActiveFlag() ||

      Boolean(getImpersonationTokenBackup()) ||

      Boolean(getAdminTokenBackup());



    if (!hasImpersonationArtifacts) return false;



    const adminToken = this.ensureAdminBackupFromSession();

    if (!adminToken) return false;



    if (this.isSessionUsingImpersonationBearer(adminToken)) {

      const current = useSessionStore.getState().session?.access_token;

      if (current && !getImpersonationTokenBackup()) {

        setImpersonationTokenBackup(current);

      }

      setImpersonationActiveFlag(true);

      return false;

    }



    if (getImpersonationTokenBackup() && !isImpersonationActiveFlag()) {

      clearImpersonationTokenBackup();

    }



    try {

      const response = await supportImpersonateApi.getStatus();

      const status = response.data.data;



      if (!status.active) {

        if (isImpersonationActiveFlag() || getImpersonationTokenBackup() || getAdminTokenBackup()) {

          clearImpersonationSessionFlags();

        }

        return false;

      }



      await this.resumeImpersonationFromServer(status);

      return true;

    } catch (error) {

      const httpStatus = (error as AxiosError)?.response?.status;

      if (httpStatus === 403 || httpStatus === 401) {

        return false;

      }

      if (isImpersonationActiveFlag()) {

        clearImpersonationSessionFlags();

      }

      return false;

    }

  }



  /** @deprecated Use recoverImpersonationSession */

  async syncAfterReload() {

    if (!isImpersonationActiveFlag()) return;

    await this.recoverImpersonationSession(null);

  }



  clearOnLogout() {

    clearImpersonationSessionFlags();

  }

}



export const impersonationService = new ImpersonationService();


